import React, { ReactNode } from "react";
import { Box, Typography, Link } from "@mui/material";
import { sxProps } from "./adf-renderer.style";

// ADF Type Definitions
interface AdfMark {
  type: "strong" | "em" | "underline" | "strike" | "code" | "link" | "subsup" | "textColor" | "backgroundColor";
  attrs?: {
    href?: string;
    title?: string;
    type?: "sub" | "sup";
    color?: string;
  };
}

interface AdfNode {
  type: string;
  content?: AdfNode[];
  text?: string;
  marks?: AdfMark[];
  attrs?: {
    level?: number;
    language?: string;
    panelType?: "info" | "note" | "warning" | "success" | "error";
    order?: number;
    shortName?: string;
    text?: string;
    id?: string;
    accessLevel?: string;
    timestamp?: string;
    color?: string;
    localId?: string;
    style?: string;
    url?: string;
    title?: string;
  };
}

interface AdfDocument {
  type: "doc";
  version: number;
  content: AdfNode[];
}

export type AdfRendererProps = {
  content: AdfDocument | string | null | undefined;
};

/**
 * Renders Atlassian Document Format (ADF) content to React components
 */
export const AdfRenderer = ({ content }: AdfRendererProps) => {
  if (!content) {
    return null;
  }

  // If content is a string, render it directly
  if (typeof content === "string") {
    return <Typography variant="body1">{content}</Typography>;
  }

  // If it's not a valid ADF document, try to render as string
  if (typeof content !== "object" || content.type !== "doc" || !content.content) {
    return <Typography variant="body1">{String(content)}</Typography>;
  }

  return <Box sx={sxProps.container}>{renderNodes(content.content)}</Box>;
};

/**
 * Renders an array of ADF nodes
 */
const renderNodes = (nodes: AdfNode[]): ReactNode[] => {
  return nodes.map((node, index) => renderNode(node, index));
};

/**
 * Renders a single ADF node based on its type
 */
const renderNode = (node: AdfNode, index: number): ReactNode => {
  switch (node.type) {
    case "paragraph":
      return renderParagraph(node, index);
    case "text":
      return renderText(node, index);
    case "hardBreak":
      return <br key={index} />;
    case "bulletList":
      return renderBulletList(node, index);
    case "orderedList":
      return renderOrderedList(node, index);
    case "listItem":
      return renderListItem(node, index);
    case "heading":
      return renderHeading(node, index);
    case "blockquote":
      return renderBlockquote(node, index);
    case "codeBlock":
      return renderCodeBlock(node, index);
    case "panel":
      return renderPanel(node, index);
    case "rule":
      return <Box key={index} component="hr" sx={sxProps.rule} />;
    case "table":
      return renderTable(node, index);
    case "tableRow":
      return renderTableRow(node, index);
    case "tableHeader":
      return renderTableHeader(node, index);
    case "tableCell":
      return renderTableCell(node, index);
    case "mention":
      return renderMention(node, index);
    case "emoji":
      return renderEmoji(node, index);
    case "date":
      return renderDate(node, index);
    case "status":
      return renderStatus(node, index);
    case "inlineCard":
      return renderInlineCard(node, index);
    case "mediaGroup":
    case "mediaSingle":
      return renderMedia(node, index);
    case "expand":
    case "nestedExpand":
      return renderExpand(node, index);
    default:
      // For unknown node types, try to render content if available
      if (node.content) {
        return <React.Fragment key={index}>{renderNodes(node.content)}</React.Fragment>;
      }
      return null;
  }
};

/**
 * Applies marks (formatting) to text content
 */
const applyMarks = (text: string, marks: AdfMark[] | undefined, index: number): ReactNode => {
  if (!marks || marks.length === 0) {
    return text;
  }

  let result: ReactNode = text;

  // Apply marks in reverse order so they nest correctly
  for (let i = marks.length - 1; i >= 0; i--) {
    const mark = marks[i];
    result = applyMark(result, mark, `${index}-mark-${i}`);
  }

  return result;
};

/**
 * Applies a single mark to content
 */
const applyMark = (content: ReactNode, mark: AdfMark, key: string): ReactNode => {
  switch (mark.type) {
    case "strong":
      return <strong key={key}>{content}</strong>;
    case "em":
      return <em key={key}>{content}</em>;
    case "underline":
      return <u key={key}>{content}</u>;
    case "strike":
      return <s key={key}>{content}</s>;
    case "code":
      return (
        <Box component="code" key={key} sx={sxProps.inlineCode}>
          {content}
        </Box>
      );
    case "link":
      return (
        <Link
          key={key}
          href={mark.attrs?.href}
          target="_blank"
          rel="noopener noreferrer"
          title={mark.attrs?.title}
          sx={sxProps.link}
        >
          {content}
        </Link>
      );
    case "subsup":
      if (mark.attrs?.type === "sub") {
        return <sub key={key}>{content}</sub>;
      }
      return <sup key={key}>{content}</sup>;
    case "textColor":
      return (
        <span key={key} style={{ color: mark.attrs?.color }}>
          {content}
        </span>
      );
    case "backgroundColor":
      return (
        <span key={key} style={{ backgroundColor: mark.attrs?.color }}>
          {content}
        </span>
      );
    default:
      return content;
  }
};

/**
 * Renders a paragraph node
 */
const renderParagraph = (node: AdfNode, index: number): ReactNode => {
  if (!node.content || node.content.length === 0) {
    return <Typography key={index} variant="body1" sx={sxProps.paragraph}>&nbsp;</Typography>;
  }

  return (
    <Typography key={index} variant="body1" component="p" sx={sxProps.paragraph}>
      {node.content.map((child, childIndex) => renderNode(child, childIndex))}
    </Typography>
  );
};

/**
 * Renders a text node with marks
 */
const renderText = (node: AdfNode, index: number): ReactNode => {
  if (!node.text) {
    return null;
  }
  return <React.Fragment key={index}>{applyMarks(node.text, node.marks, index)}</React.Fragment>;
};

/**
 * Renders a bullet list
 */
const renderBulletList = (node: AdfNode, index: number): ReactNode => {
  if (!node.content) {
    return null;
  }

  return (
    <Box component="ul" key={index} sx={sxProps.bulletList}>
      {renderNodes(node.content)}
    </Box>
  );
};

/**
 * Renders an ordered list
 */
const renderOrderedList = (node: AdfNode, index: number): ReactNode => {
  if (!node.content) {
    return null;
  }

  return (
    <Box component="ol" key={index} sx={sxProps.orderedList} start={node.attrs?.order}>
      {renderNodes(node.content)}
    </Box>
  );
};

/**
 * Renders a list item
 */
const renderListItem = (node: AdfNode, index: number): ReactNode => {
  if (!node.content) {
    return null;
  }

  return (
    <Box component="li" key={index} sx={sxProps.listItem}>
      {renderNodes(node.content)}
    </Box>
  );
};

/**
 * Renders a heading node
 */
const renderHeading = (node: AdfNode, index: number): ReactNode => {
  const level = node.attrs?.level || 1;
  const variant = `h${Math.min(level, 6)}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

  return (
    <Typography key={index} variant={variant} component={variant} sx={sxProps.heading}>
      {node.content ? node.content.map((child, childIndex) => renderNode(child, childIndex)) : null}
    </Typography>
  );
};

/**
 * Renders a blockquote
 */
const renderBlockquote = (node: AdfNode, index: number): ReactNode => {
  if (!node.content) {
    return null;
  }

  return (
    <Box component="blockquote" key={index} sx={sxProps.blockquote}>
      {renderNodes(node.content)}
    </Box>
  );
};

/**
 * Renders a code block
 */
const renderCodeBlock = (node: AdfNode, index: number): ReactNode => {
  const language = node.attrs?.language;
  const codeContent = node.content
    ?.map((child) => (child.type === "text" ? child.text : ""))
    .join("") || "";

  return (
    <Box key={index} sx={sxProps.codeBlock}>
      {language && (
        <Typography variant="caption" sx={sxProps.codeLanguage}>
          {language}
        </Typography>
      )}
      <Box component="pre" sx={sxProps.pre}>
        <code>{codeContent}</code>
      </Box>
    </Box>
  );
};

/**
 * Renders a panel (info, note, warning, success, error)
 */
const renderPanel = (node: AdfNode, index: number): ReactNode => {
  if (!node.content) {
    return null;
  }

  const panelType = node.attrs?.panelType || "info";
  const panelSx = {
    ...sxProps.panel,
    ...sxProps[`panel_${panelType}` as keyof typeof sxProps],
  };

  return (
    <Box key={index} sx={panelSx}>
      {renderNodes(node.content)}
    </Box>
  );
};

/**
 * Renders a table
 */
const renderTable = (node: AdfNode, index: number): ReactNode => {
  if (!node.content) {
    return null;
  }

  return (
    <Box key={index} sx={sxProps.tableContainer}>
      <Box component="table" sx={sxProps.table}>
        <tbody>{renderNodes(node.content)}</tbody>
      </Box>
    </Box>
  );
};

/**
 * Renders a table row
 */
const renderTableRow = (node: AdfNode, index: number): ReactNode => {
  if (!node.content) {
    return null;
  }

  return <tr key={index}>{renderNodes(node.content)}</tr>;
};

/**
 * Renders a table header cell
 */
const renderTableHeader = (node: AdfNode, index: number): ReactNode => {
  return (
    <Box component="th" key={index} sx={sxProps.tableHeader}>
      {node.content ? renderNodes(node.content) : null}
    </Box>
  );
};

/**
 * Renders a table cell
 */
const renderTableCell = (node: AdfNode, index: number): ReactNode => {
  return (
    <Box component="td" key={index} sx={sxProps.tableCell}>
      {node.content ? renderNodes(node.content) : null}
    </Box>
  );
};

/**
 * Renders a mention
 */
const renderMention = (node: AdfNode, index: number): ReactNode => {
  const text = node.attrs?.text || "@unknown";

  return (
    <Box component="span" key={index} sx={sxProps.mention}>
      {text}
    </Box>
  );
};

/**
 * Renders an emoji
 */
const renderEmoji = (node: AdfNode, index: number): ReactNode => {
  const shortName = node.attrs?.shortName || "";
  const text = node.attrs?.text || shortName;

  return (
    <span key={index} role="img" aria-label={shortName}>
      {text}
    </span>
  );
};

/**
 * Renders a date
 */
const renderDate = (node: AdfNode, index: number): ReactNode => {
  const timestamp = node.attrs?.timestamp;

  if (!timestamp) {
    return null;
  }

  const date = new Date(parseInt(timestamp, 10));
  const formattedDate = date.toLocaleDateString();

  return (
    <Box component="span" key={index} sx={sxProps.date}>
      {formattedDate}
    </Box>
  );
};

/**
 * Renders a status badge
 */
const renderStatus = (node: AdfNode, index: number): ReactNode => {
  const text = node.attrs?.text || "";
  const color = node.attrs?.color || "neutral";

  return (
    <Box component="span" key={index} sx={{ ...sxProps.status, ...sxProps[`status_${color}` as keyof typeof sxProps] }}>
      {text}
    </Box>
  );
};

/**
 * Renders an inline card (link preview)
 */
const renderInlineCard = (node: AdfNode, index: number): ReactNode => {
  const url = node.attrs?.url;

  if (!url) {
    return null;
  }

  return (
    <Link key={index} href={url} target="_blank" rel="noopener noreferrer" sx={sxProps.inlineCard}>
      {url}
    </Link>
  );
};

/**
 * Renders media (placeholder - actual implementation depends on your media handling)
 */
const renderMedia = (node: AdfNode, index: number): ReactNode => {
  // Media rendering depends on how your app handles media files
  // This is a placeholder that shows media exists
  return (
    <Box key={index} sx={sxProps.mediaPlaceholder}>
      <Typography variant="caption">[Media content]</Typography>
    </Box>
  );
};

/**
 * Renders an expand/collapse section
 */
const renderExpand = (node: AdfNode, index: number): ReactNode => {
  if (!node.content) {
    return null;
  }

  // Simple rendering without collapse functionality
  // You can enhance this with actual expand/collapse behavior
  return (
    <Box key={index} sx={sxProps.expand}>
      {node.attrs?.title && (
        <Typography variant="subtitle2" sx={sxProps.expandTitle}>
          {node.attrs.title}
        </Typography>
      )}
      {renderNodes(node.content)}
    </Box>
  );
};

export default AdfRenderer;
